import { useRef, useState } from "react";
import { ChartCard } from "./chart-card";
import { ProgressList, sortRowsLocally, type SortMode } from "./progress-list";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
} from "recharts";
import { BAR_VALUE_LABEL, NO_ANIMATION } from "./chart-ticks";
import { Download } from "lucide-react";
import { type StatisticsByAdmissionMethod } from "@/lib/statistics-api.service";
import {
	StatisticsFilterControls,
	type StatisticsFilterType,
	type StatisticsFilters,
} from "./StatisticsControls";
import { COLORS, downloadChartPng } from "./utils";

interface AdmissionTabProps {
	admissionStats: StatisticsByAdmissionMethod[];
	onFilterChange?: (filter: StatisticsFilterType) => void;
	filters?: StatisticsFilters;
	onFiltersChange?: (filters: StatisticsFilters) => void;
}

export default function AdmissionTab({
	admissionStats,
	onFilterChange,
	filters = {},
	onFiltersChange,
}: AdmissionTabProps) {
	const admissionRef = useRef<HTMLDivElement>(null);
	const [filterType, setFilterType] = useState<StatisticsFilterType>("total");
	const [chartLimit, setChartLimit] = useState(10);
	const [sortBy, setSortBy] = useState<SortMode>("count");

	const handleFilterChange = (newFilter: StatisticsFilterType) => {
		setFilterType(newFilter);
		onFilterChange?.(newFilter);
	};

	const getChartData = () => {
		let chartData = [];

		switch (filterType) {
			case "gender_status":
				chartData = admissionStats.map((method) => ({
					admissionMethod: method.admissionMethod,
					Nam: method.male,
					Nữ: method.female,
				}));
				break;
			case "male_only":
				chartData = admissionStats.map((method) => ({
					admissionMethod: method.admissionMethod,
					Nam: method.male,
				}));
				break;
			case "female_only":
				chartData = admissionStats.map((method) => ({
					admissionMethod: method.admissionMethod,
					Nữ: method.female,
				}));
				break;
			case "enrollment_status":
				chartData = admissionStats.map((method) => ({
					admissionMethod: method.admissionMethod,
					"Đã tiếp nhận": method.received,
					"Chưa tiếp nhận": method.pending,
				}));
				break;
			case "payment_status":
				chartData = admissionStats.map((method) => ({
					admissionMethod: method.admissionMethod,
					"Đã đóng học phí": method.paid,
					"Chưa đóng học phí": method.unpaid,
				}));
				break;
			case "paid_only":
				chartData = admissionStats.map((method) => ({
					admissionMethod: method.admissionMethod,
					"Đã đóng học phí": method.paid,
				}));
				break;
			case "unpaid_only":
				chartData = admissionStats.map((method) => ({
					admissionMethod: method.admissionMethod,
					"Chưa đóng học phí": method.unpaid,
				}));
				break;
			case "received_only":
				chartData = admissionStats.map((method) => ({
					admissionMethod: method.admissionMethod,
					"Đã tiếp nhận": method.received,
				}));
				break;
			default:
				chartData = admissionStats.map((method) => ({
					admissionMethod: method.admissionMethod,
					"Đã tiếp nhận": method.received,
					"Chưa tiếp nhận": method.pending,
				}));
		}

		return chartData.slice(0, chartLimit);
	};

	return (
		<TabsContent value="admission">
			<div ref={admissionRef} className="space-y-6">
				<div className="flex justify-between items-center">
					<h2 className="text-2xl font-semibold">Thống kê theo diện xét tuyển</h2>
					<Button
						onClick={() => downloadChartPng(admissionRef, "thong-ke-theo-dien-xet-tuyen")}
						variant="outline"
						size="sm"
						className="flex items-center gap-2"
					>
						<Download className="h-4 w-4" />
						Tải PNG
					</Button>
				</div>

				<StatisticsFilterControls
					filterType={filterType}
					onFilterChange={handleFilterChange}
					chartLimit={chartLimit}
					onChartLimitChange={setChartLimit}
					showChartLimit={true}
					filters={filters}
					onFiltersChange={onFiltersChange}
					showFilters={true}
				/>

				<ChartCard title="Thống kê theo diện xét tuyển" filename="thong-ke-theo-dien-xet-tuyen">
					<ResponsiveContainer width="100%" height={400}>
						<BarChart data={getChartData()} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="admissionMethod" />
							<YAxis />
							<Tooltip />
							<Legend />
							{Object.keys(getChartData()[0] || {})
								.filter((key) => key !== "admissionMethod")
								.map((key, index) => (
									<Bar
										{...NO_ANIMATION}
										key={key}
										dataKey={key}
										fill={COLORS[index % COLORS.length]}
										label={BAR_VALUE_LABEL}
									/>
								))}
						</BarChart>
					</ResponsiveContainer>
				</ChartCard>

				<ProgressList
					title="Tiến độ theo phương thức xét tuyển"
					description={`${Math.min(admissionStats.length, chartLimit)} phương thức`}
					remainderLabel="Chưa nộp hồ sơ, chưa đóng phí"
					sortBy={sortBy}
					onSortByChange={setSortBy}
					rows={sortRowsLocally(
						admissionStats.map((method: StatisticsByAdmissionMethod) => ({
							id: method.admissionMethod,
							label: method.admissionMethod,
							total: method.total,
							primary: method.received,
							segments: [
								{
									key: "received",
									label: "Đã tiếp nhận",
									value: method.received,
									className: "bg-green-600",
								},
								{
									key: "paidButNotReceived",
									label: "Đã đóng phí, chưa tiếp nhận",
									value: method.paidButNotReceived,
									className: "bg-green-300 dark:bg-green-800",
								},
							],
							details: [
								{ label: "Tổng", value: method.total },
								{
									label: "Đã tiếp nhận",
									value: method.received,
									className: "text-green-700 dark:text-green-400",
								},
								{
									label: "Chưa tiếp nhận",
									value: method.pending,
									className: "text-orange-700 dark:text-orange-400",
								},
								{
									label: "Đã đóng phí chưa tiếp nhận",
									value: method.paidButNotReceived,
									className: "text-orange-700 dark:text-orange-400",
								},
								{
									label: "Chưa nộp, chưa đóng phí",
									value: method.notReceivedAndNotPaid,
									className: "text-muted-foreground",
								},
								{ label: "Nam", value: method.male, className: "text-blue-600 dark:text-blue-400" },
								{
									label: "Nữ",
									value: method.female,
									className: "text-pink-600 dark:text-pink-400",
								},
							],
						})),
						sortBy,
						// Cắt SAU khi sắp, để "10 mục" là 10 mục đầu của thứ tự đang chọn.
					).slice(0, chartLimit)}
				/>
			</div>
		</TabsContent>
	);
}
